<template>
  <div class="tender-header">
    <!-- The label is hidden on tablets and phones; aria-label keeps the button named there. -->
    <button @click="backToMode" class="tender-header__back" :aria-label="backText">
      <BaseIcon class="tender-header__back-icon" name="arrow-left" />
      <span class="tender-header__back-text"> {{ backText }} </span>
    </button>
    <div class="tender-header__actions">
      <div class="tender-header__assignation-block">
        <BaseAssignation
          ref="assignation"
          :value="assignees"
          @change="onAssign"
          :attach="'.tender-header__assignation-block'"
          tooltipPlacement="bottom"
        />
      </div>
      <BaseRoundButton
        v-if="hasUserTender"
        @click.native="onFollowClick()"
        :size="44"
        :is-light="isFollowed"
        :tooltip-text="isFollowed ? $t('actions.unfollow') : $t('actions.follow')"
        :tooltip-subtitle="$t('page.tenders.follow-tooltip-subtitle')"
        tooltipPlacement="bottom"
        class="tender-header__actions-item"
        icon="bookmark"
        is-white
        id="followAction"
      />
      <div v-else class="tender-header__actions-placeholder" />

      <BaseRoundButton
        v-if="hasUserTender"
        @click.native="onShareClick()"
        :size="44"
        :is-light="isShared"
        :tooltip-text="isShared ? $t('actions.unshare') : $t('actions.share')"
        :tooltip-subtitle="$t('page.tenders.share-tooltip-subtitle')"
        tooltipPlacement="bottom"
        class="tender-header__actions-item"
        icon="share-2"
        is-white
        id="shareAction"
      />
      <div v-else class="tender-header__actions-placeholder" />

      <BaseRoundButton
        v-if="hasUserTender"
        @click.native="onReadClick()"
        :size="44"
        :is-light="isRead"
        :tooltip-text="isRead ? $t('actions.unread') : $t('actions.read')"
        tooltipPlacement="bottom"
        class="tender-header__actions-item"
        :icon="isRead ? 'eye' : 'eye-off'"
        is-white
        id="readAction"
      />
      <div v-else class="tender-header__actions-placeholder" />

      <span class="tender-header__actions-seperator"></span>

      <BaseRoundButton
        v-if="isClipboardSupported"
        @click.native="copyLinkToClipBoard()"
        :size="44"
        :tooltip-text="$t('actions.copyLink')"
        tooltipPlacement="bottom"
        class="tender-header__actions-item"
        icon="copy"
        is-white
        id="copyAction"
      />
      <div v-else class="tender-header__actions-placeholder" />

      <div class="tender-header__actions__container">
        <v-popover
          v-if="hasTenantTender"
          placement="bottom"
          trigger="hover"
          popoverClass="tender-header__vpopover-tooltip"
        >
          <BaseRoundButton
            @click.native="onArchiveClick"
            :size="44"
            icon="archive"
            :is-red="!isArchived"
            is-white
            id="archiveAction"
          />
          <template v-slot:popover>
            <span class="tender-header__tooltip-text">
              {{ isArchived ? $t('actions.tender.unarchive') : $t('actions.tender.archive') }}</span
            >
            <div v-if="isMacOS && !isArchived" class="tender-header__shortcuts">
              <BaseShortcut isIcon value="command" />
              <BaseShortcut isPlus value="plus" />
              <BaseShortcut isIcon value="delete" />
            </div>
            <div v-else-if="!isArchived" class="tender-header__shortcuts">
              <BaseShortcut value="delete" />
            </div>
          </template>
        </v-popover>
        <div v-else class="tender-header__actions-placeholder" />
      </div>

      <div v-if="tenantHasOpportunitiesAccess()" class="tender-header__opportunity">
        <!-- Already converted: lead to the opportunity instead of a disabled button. -->
        <BaseButton
          v-if="linkedOpportunities.length === 1"
          @click.native="goToOpportunity(linkedOpportunities[0])"
          is-white
          is-square
          has-icon-left
          class="tender-header__actions-item"
        >
          <BaseIcon name="target" />
          {{ $tc('actions.tender.view-opportunity', 1) }}
        </BaseButton>
        <v-popover
          v-else-if="linkedOpportunities.length > 1"
          placement="bottom-end"
          trigger="click"
          popoverClass="tender-header__opportunities-popover"
        >
          <BaseButton is-white is-square has-icon-left class="tender-header__actions-item">
            <BaseIcon name="target" />
            {{
              $tc('actions.tender.view-opportunity', linkedOpportunities.length, { count: linkedOpportunities.length })
            }}
          </BaseButton>
          <template v-slot:popover>
            <ul class="tender-header__opportunities-list">
              <li v-for="opportunity in linkedOpportunities" :key="opportunity.id">
                <button v-close-popover @click="goToOpportunity(opportunity)">
                  {{ opportunity.name }}
                  <!-- Names can repeat (they default to the tender title); the date tells them apart. -->
                  <span class="tender-header__opportunities-date">{{
                    formatShortDateTime(opportunity.createdAt)
                  }}</span>
                </button>
              </li>
            </ul>
          </template>
        </v-popover>
        <BaseButton
          v-else
          @click.native="onCreateOpportunityClick()"
          is-white
          is-square
          has-icon-left
          :disabled="pending"
          class="tender-header__actions-item"
        >
          <BaseIcon name="target" />
          {{ $tc('actions.tender.convert-to-opportunity') }}
        </BaseButton>
      </div>

      <div class="tender-header__mobile-actions" v-closable="hideMobileActions">
        <BaseRoundButton
          @click.native="toggleActions"
          :size="44"
          class="tender-header__actions-item"
          icon="more-horizontal"
          is-white
        />
        <transition name="dropdown-basic">
          <ul class="tender-header__actions-list" v-show="mobileActionsVisible">
            <li v-if="hasUserTender" class="tender-header__actions-list-item">
              <button @click="onShareClick">
                <BaseIcon :class="{ isRead: isShared }" name="share-2" />
                {{ isShared ? $t('actions.unshare') : $t('actions.share') }}
              </button>
            </li>
            <li class="tender-header__actions-list-item">
              <button @click="onReadClick">
                <BaseIcon
                  v-if="hasUserTender"
                  :size="44"
                  :class="{ isRead: isRead }"
                  :name="isRead ? 'eye' : 'eye-off'"
                />
                {{ isRead ? $t('actions.unread') : $t('actions.read') }}
              </button>
            </li>
            <li class="tender-header__actions-list-item">
              <button @click="copyLinkToClipBoard">
                <BaseIcon :size="44" v-if="isClipboardSupported" name="copy" />
                {{ $t('actions.copyLink') }}
              </button>
            </li>
            <li class="tender-header__actions-list-item" :class="{ 'is-destructive': !isArchived }">
              <button @click="onArchiveClick">
                <BaseIcon :size="44" name="archive" />
                {{ isArchived ? $t('actions.tender.unarchive') : $t('actions.tender.archive') }}
              </button>
            </li>
          </ul>
        </transition>
      </div>
    </div>

    <!-- Phones: the header keeps back, assignees and save; everything else is here. -->
    <BaseBottomSheet v-if="isPhone" v-model="actionsSheetOpen" :title="tender ? removeMarks(tender.title) : ''">
      <ul class="tender-header__sheet">
        <template v-if="tenantHasOpportunitiesAccess()">
          <li v-for="opportunity in linkedOpportunities" :key="opportunity.id">
            <button type="button" class="is-primary" @click="fromSheet(() => goToOpportunity(opportunity))">
              <BaseIcon name="target" aria-hidden="true" />
              <span>
                {{ linkedOpportunities.length === 1 ? $tc('actions.tender.view-opportunity', 1) : opportunity.name }}
                <span v-if="linkedOpportunities.length > 1" class="tender-header__opportunities-date">
                  {{ formatShortDateTime(opportunity.createdAt) }}
                </span>
              </span>
            </button>
          </li>
          <li v-if="!linkedOpportunities.length">
            <button type="button" class="is-primary" :disabled="pending" @click="fromSheet(onCreateOpportunityClick)">
              <BaseIcon name="target" aria-hidden="true" />
              {{ $tc('actions.tender.convert-to-opportunity') }}
            </button>
          </li>
        </template>
        <li v-if="hasUserTender">
          <button type="button" :class="{ 'is-active': isShared }" @click="fromSheet(onShareClick)">
            <BaseIcon name="share-2" aria-hidden="true" />
            {{ isShared ? $t('actions.unshare') : $t('actions.share') }}
          </button>
        </li>
        <li v-if="hasUserTender">
          <button type="button" @click="fromSheet(onReadClick)">
            <BaseIcon :name="isRead ? 'eye-off' : 'eye'" aria-hidden="true" />
            {{ isRead ? $t('actions.unread') : $t('actions.read') }}
          </button>
        </li>
        <li v-if="isClipboardSupported">
          <button type="button" @click="fromSheet(copyLinkToClipBoard)">
            <BaseIcon name="copy" aria-hidden="true" />
            {{ $t('actions.copyLink') }}
          </button>
        </li>
        <li v-if="hasTenantTender">
          <button type="button" :class="{ 'is-destructive': !isArchived }" @click="fromSheet(onArchiveClick)">
            <BaseIcon name="archive" aria-hidden="true" />
            {{ isArchived ? $t('actions.tender.unarchive') : $t('actions.tender.archive') }}
          </button>
        </li>
      </ul>
    </BaseBottomSheet>
  </div>
</template>

<script>
import BaseAssignation from '@/components/base/BaseAssignation';
import BaseIcon from '@/components/base/BaseIcon';
import BaseShortcut from '@/components/base/BaseShortcut';
import BaseBottomSheet from '@/components/base/BaseBottomSheet.vue';
import BaseButton from '@/components/base/BaseButton';
import BaseRoundButton from '@/components/base/BaseRoundButton';

import { tenantHasOpportunitiesAccess } from '@/util/module-access-util';
import { bus } from '@/main';
import { copyTextToClipboard, isClipboardSupported } from '@/util/clipboard';
import { Modes } from '@/models/modes';
import { VPopover } from 'v-tooltip';
import { shareLink } from '@/models/tenders';
import { removeMarks } from '@/util/mark-util';
import { formatShortDateTime } from '@/util/date-util';
import { viewport } from '@/util/viewport';

import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'TenderHeader',
  components: {
    BaseAssignation,
    BaseIcon,
    BaseShortcut,
    BaseBottomSheet,
    BaseButton,
    BaseRoundButton,
    VPopover,
  },
  props: {
    backToMode: {
      type: Function,
      required: false,
    },
    tender: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      mobileActionsVisible: false,
      actionsSheetOpen: false,
    };
  },
  computed: {
    ...mapGetters('modes', ['mode']),
    ...mapGetters('tenderOpportunities', ['tenderOpportunities', 'pending']),
    isPhone() {
      return viewport.size === 'phone';
    },
    backText() {
      let backText;
      switch (true) {
        case this.isExploreMode:
          backText = 'menu.explore';
          break;
        case this.isInboxMode:
          backText = 'menu.inbox';
          break;
        case this.isOpportunitiesActive:
          backText = 'menu.opportunities';
          break;
        default:
          backText = 'menu.inbox';
          break;
      }
      return this.$t(backText);
    },
    hasUserTender() {
      return this.tender != null && this.tender.userTender != null;
    },
    hasTenantTender() {
      return this.tender != null && this.tender.tenantTender != null;
    },
    linkedOpportunities() {
      return this.tenderOpportunities;
    },
    isRead() {
      return this.hasUserTender && !!this.tender.userTender.read;
    },
    isFollowed() {
      return this.hasUserTender && !!this.tender.userTender.followed;
    },
    isShared() {
      return this.hasTenantTender && !!this.tender.tenantTender.shared;
    },
    isArchived() {
      return this.hasTenantTender && !!this.tender.tenantTender.archived;
    },
    isMacOS() {
      return navigator.userAgent.toLowerCase().indexOf('mac') >= 0;
    },
    isExploreMode() {
      return this.mode === Modes.EXPLORE.name;
    },
    isInboxMode() {
      return this.mode === Modes.INBOX.name;
    },
    isOpportunitiesActive() {
      return this.mode === Modes.OPPORTUNITIES.name;
    },
    assignees() {
      return this.tender.assignees;
    },
  },
  methods: {
    ...mapActions('tender', [
      'assign',
      'read',
      'archive',
      'unarchive',
      'unread',
      'follow',
      'unfollow',
      'share',
      'unshare',
    ]),
    ...mapActions('tenderOpportunities', ['addOpportunity', 'getTenderOpportunities']),
    isClipboardSupported,
    tenantHasOpportunitiesAccess,
    removeMarks,
    copyLinkToClipBoard() {
      this.$track.tenderCopyLink();
      const text = shareLink(this.tender.id);

      copyTextToClipboard(
        text,
        () => this.$toast.show(this.$t('toast.copyLink')),
        (error) => this.$toast.error(error),
      );
    },
    // A failed save is reported, and the avatars fall back to what is stored.
    onAssign(userIds) {
      return this.assign({ tenderId: this.tender.id, userIds }).catch((error) => {
        this.$toast.error(error);
        if (this.$refs.assignation) this.$refs.assignation.reset();
      });
    },
    onReadClick() {
      const id = this.tender.id;

      if (this.isRead) {
        this.$track.tenderUnread();
        this.unread({ id })
          .then(() => this.$toast.cancel(this.$tc('toast.unread'), () => this.read({ id })))
          .catch((error) => this.$toast.error(error));
      } else {
        this.$track.tenderRead();
        this.read({ id })
          .then(() => this.$toast.cancel(this.$tc('toast.read'), () => this.unread({ id })))
          .catch((error) => this.$toast.error(error));
      }
    },
    onFollowClick() {
      const id = this.tender.id;

      if (this.isFollowed) {
        this.$track.tenderUnfollow('tender');
        this.unfollow({ id })
          .then(() =>
            this.$toast.cancel(this.$tc('toast.unfollow'), () => {
              this.follow({ id }).then(() => bus.$emit('refresh-page-requested'));
            }),
          )
          .catch((error) => this.$toast.error(error));
      } else {
        this.$track.tenderFollow('tender');
        this.follow({ id })
          .then(() => this.$toast.cancel(this.$tc('toast.follow'), () => this.unfollow({ id })))
          .catch((error) => this.$toast.error(error));
      }
    },
    onShareClick() {
      const id = this.tender.id;

      if (this.isShared) {
        this.$track.tenderUnshare('tender');
        this.unshare({ id })
          .then(() =>
            this.$toast.cancel(this.$tc('toast.unshare'), () =>
              this.share({ id }).then(() => bus.$emit('refresh-page-requested')),
            ),
          )
          .catch((error) => this.$toast.error(error));
      } else {
        this.$track.tenderShare('tender');
        this.share({ id })
          .then(() => this.$toast.cancel(this.$tc('toast.share'), () => this.unshare({ id })))
          .catch((error) => this.$toast.error(error));
      }
    },
    onCreateOpportunityClick() {
      if (this.pending) return;

      const oppName = removeMarks(this.tender.title);
      this.addOpportunity({ name: oppName, tenderId: this.tender.id })
        .then(() => {
          this.$toast.success(this.$tc('page.opportunity-details.opportunity-manipulations.add-success', 1));
        })
        .catch((error) => {
          this.$toast.error(error);
          // The opportunity may have been saved before the error. Reload, so the
          // button offers to open it rather than inviting a duplicate.
          this.getTenderOpportunities({ tenderId: this.tender.id }).catch(() => {});
        });
    },
    formatShortDateTime,
    goToOpportunity(opportunity) {
      this.$router.push({ name: 'opportunity-details', params: { opportunityId: opportunity.id } });
    },
    onArchiveClick() {
      this.$track.tenderArchive();

      const id = this.tender.id;

      if (this.isArchived) {
        this.unarchive({ id })
          .then(() => {
            this.$toast.cancel(this.$tc('toast.unarchive'), () =>
              this.archive({ id }).then(() => bus.$emit('refresh-page-requested')),
            );
          })
          .then(() => {
            this.$emit('navigate');
            bus.$emit('refresh-page-requested');
          })
          .catch((error) => this.$toast.error(error));
      } else {
        this.archive({ id })
          .then(() => {
            this.$toast.cancel(this.$tc('toast.archive'), () =>
              this.unarchive({ id }).then(() => bus.$emit('refresh-page-requested')),
            );
          })
          .then(() => {
            this.$emit('navigate');
            bus.$emit('refresh-page-requested');
          })
          .catch((error) => this.$toast.error(error));
      }
    },
    toggleActions() {
      if (this.isPhone) this.actionsSheetOpen = true;
      else this.mobileActionsVisible = !this.mobileActionsVisible;
    },
    fromSheet(action) {
      this.actionsSheetOpen = false;
      action();
    },
    hideMobileActions() {
      this.mobileActionsVisible = false;
    },
  },
};
</script>

<style lang="scss">
.tender-header {
  background: $color-white;
  border-bottom: 1px solid $color-border;
  display: flex;
  height: $header-height;
  align-items: center;
  position: sticky;
  top: $menu-top-bar-height;
  width: 100%;
  z-index: $header-index;
  @include top-bar-follower;

  &__vpopover-tooltip {
    padding: 8px;
    font-size: $font-size-xsmall;
    text-align: center;
    background-color: $color-normal !important;
    color: $color-white !important;
    box-shadow: $shadow-tooltip;

    .popover-arrow {
      background-color: $color-normal !important;
    }
  }

  &__opportunities-popover {
    background-color: $color-white;
    border-radius: $border-radius-small;
    box-shadow: $shadow-center;

    .popover-arrow {
      display: none;
    }
  }

  &__opportunities-list {
    list-style: none;
    margin: 0;
    padding: 8px 0;
    max-width: 360px;

    button {
      display: block;
      width: 100%;
      padding: 8px 16px;
      text-align: left;
      transition: color 200ms ease;

      &:hover {
        color: $color-accent;
      }
    }
  }

  &__opportunities-date {
    display: block;
    color: $color-normal-pale;
    font-size: $font-size-xsmall;
  }

  &__shortcuts {
    margin: 5px;
  }

  &__back {
    align-items: center;
    border-right: 1px solid $color-border;
    color: $color-normal;
    cursor: pointer;
    display: flex;
    height: 100%;
    justify-content: center;
    line-height: 1;
    outline: none;
    padding: 16px 32px;
    text-align: left;
    text-decoration: none;
    flex-grow: 0;
    min-width: max-content;

    &:hover,
    &:active,
    &:focus {
      color: $color-accent;
    }

    &-icon {
      color: $color-neutral;
      margin-right: 8px;
    }

    &-text {
      font-size: 14px;
      font-weight: bold;
      text-transform: uppercase;
    }
  }

  &__assignation-block {
    display: flex;
    justify-content: flex-start;
    min-width: 32px;
    margin: 0 16px;
  }

  &__actions {
    align-items: center;
    display: flex;
    justify-content: flex-end;
    flex-grow: 0;
    min-width: max-content;
    padding: 0 32px;
    margin-left: auto;

    &__container {
      align-items: center;
      display: flex;
      flex-direction: row;
      margin-right: 8px;
    }

    &-item {
      + .tender-header__actions-item {
        margin-left: 4px;
      }
    }

    &-placeholder {
      height: 44px;
      width: 44px;
    }

    &-seperator {
      background: $color-border;
      display: block;
      height: 24px;
      margin: 0 8px;
      width: 1px;
    }
  }

  &__mobile-actions {
    display: none;
  }

  &__link {
    align-items: center;
    display: flex;
    margin-left: auto;
    padding: 0 8px;
  }

  &.is-content {
    background: $color-white;
    border-bottom: 1px solid $color-border;
    display: flex;
    height: $header-height;
    position: relative !important;
    width: 100%;
  }

  // Tablets and phones: the back button keeps its arrow only, and the secondary actions
  // move into the "⋯" menu, so the header fits the screen.
  // `& &__…`: other components reuse these classes (the opportunity header's back button,
  // the inbox's buttons), and these rules are for this header only.
  @include screen-below('laptop') {
    &__back {
      padding: 16px 24px;
    }
    & &__back-text {
      display: none;
    }
    & &__back-icon {
      margin: 0;
    }
    &__actions {
      justify-content: flex-end;
      padding: 16px;
    }
    &__assignation-block {
      margin: 0 8px;
    }
    &__button-link {
      text-decoration: none;
      color: $color-normal;
      align-content: center;
      align-items: center;
      display: flex;

      &:hover {
        color: $color-accent;
        transition: color 200ms;
      }
    }
    &__mobile-actions {
      display: block;
      margin-left: 16px;
    }

    &__actions-list {
      background-color: $color-white;
      border-radius: $border-radius-small;
      box-shadow: $shadow-center;
      list-style: none;
      padding: 0 1rem;
      position: absolute;
      margin: 0;
      right: 32px;
      top: 120%;
      width: 320px;
    }

    & &__actions-item {
      margin-right: 8px;
    }

    &__actions-list-item {
      cursor: pointer;
      display: flex;
      list-style: none;
      margin: 24px 8px;
      align-self: center;
      align-items: center;
      transition: color 200ms ease;

      .feather {
        transition: color 200ms ease;
        margin-right: 16px;
      }

      button {
        align-items: center;
        display: flex;
        text-align: left;
      }

      &:hover {
        color: $color-accent;

        .feather {
          color: $color-accent;
        }
      }

      &:last-child {
        border-top: 1px solid $color-border;
        padding-top: 16px;
      }

      &.is-destructive {
        color: $color-error;

        &:hover {
          color: $color-pale-error;

          .feather {
            color: $color-pale-error;
          }
        }
      }
    }
    #shareAction,
    #archiveAction,
    #readAction,
    #copyAction,
    #externalLinkAction,
    #second-seperator {
      display: none;
    }
    &__actions-seperator {
      display: none;
    }
    &__link {
      display: none;
    }
  }

  // Global, as it always was: the inbox's results bar and its bulk toolbar have this class
  // too, and keep the spacing it gives them on narrow screens.
  @media screen and (max-width: 784px) {
    &__actions-item {
      margin-right: 8px;
    }
  }

  // Phones: a 56px bar with back, assignees, save and "⋯"; the "⋯" opens a bottom sheet
  // that also holds the opportunity action.
  @include screen-below('tablet') {
    height: 56px;

    &__back {
      padding: 0 14px;
    }
    &__actions {
      padding: 0 8px 0 0;
    }
    &__assignation-block {
      margin: 0 4px 0 8px;
    }
    & &__actions-item {
      margin-right: 4px;
    }
    &__mobile-actions {
      margin-left: 0;
    }
    &__opportunity {
      display: none;
    }
  }

  &__sheet {
    margin: 0;
    padding: 0;
    list-style: none;

    button {
      display: flex;
      align-items: center;
      gap: 12px;
      width: 100%;
      min-height: 48px;
      padding: 6px 4px;
      font-size: 15px;
      color: $color-text-body;
      text-align: left;

      .base-icon {
        flex: none;
        width: 20px;
        height: 20px;
      }

      &.is-primary {
        color: $color-accent;
        font-weight: 500;
      }

      &.is-active {
        color: $color-accent;
      }

      &.is-destructive {
        color: $color-error;
      }

      &:disabled {
        opacity: 0.5;
      }
    }

    li:last-child {
      margin-top: 4px;
      border-top: 1px solid $color-border;
    }
  }
}

.isRead {
  color: $color-accent;
}
</style>
